import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Pizza, Music, Droplets, PartyPopper, Wallet, ArrowRight, Award } from "lucide-react";
import { api, IMAGES } from "../lib/api";

const highlights = [
  { icon: Pizza, title: "Cuisine italienne", text: "Pizzas, pâtes fraîches et spécialités authentiques préparées avec passion.", to: "/la-carte", span: "md:col-span-2" },
  { icon: Music, title: "Soirées musicales", text: "Live music, piano voix, karaoké et DJ sets chaque semaine.", to: "/soirees" },
  { icon: Droplets, title: "Terrasse avec fontaine", text: "60 places en terrasse dans un cadre enchanteur.", to: "/terrasse-evenements" },
  { icon: PartyPopper, title: "Événements privés", text: "Mariages, anniversaires, repas de groupe — jusqu'à 150 places.", to: "/terrasse-evenements", span: "md:col-span-2" },
  { icon: Wallet, title: "Wallet fidélité RITAS", text: "1 € payé = 1 RITAS, +20% de bonus par carte. Cumulez et débloquez des récompenses !", to: "/profil" },
];

export default function Home() {
  const [settings, setSettings] = useState(null);
  const [weekly, setWeekly] = useState([]);
  const [events, setEvents] = useState([]);

  useEffect(() => {
    api.get("/settings").then((r) => setSettings(r.data)).catch(() => {});
    api.get("/weekly-menus").then((r) => setWeekly(r.data)).catch(() => {});
    api.get("/events").then((r) => setEvents(r.data.filter((e) => e.status === "publie").slice(0, 3))).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative min-h-[88vh] flex items-center">
        <div className="absolute inset-0">
          <img src={IMAGES.terrace1} alt="Terrasse Italian Club" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-black/60 to-black/40" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 py-24 w-full">
          <img src={IMAGES.logo} alt="Italian Club logo" className="h-28 w-28 object-contain rounded-full bg-white/95 p-1 mb-8 fade-up shadow-[0_0_30px_rgba(212,175,55,0.35)]" />
          <p className="gold-label mb-4 fade-up-1">Bar · Restaurant · Live Music — Champs-sur-Marne</p>
          <a href="https://restaurantguru.com" target="_blank" rel="noopener noreferrer" data-testid="hero-award-badge"
            className="inline-flex items-center gap-3 rounded-full border border-[#D4AF37]/50 bg-black/50 backdrop-blur-md pl-1.5 pr-5 py-1.5 mb-6 fade-up-1 hover:border-[#D4AF37] transition-colors">
            <span className="w-8 h-8 rounded-full bg-white flex items-center justify-center"><Award size={16} className="text-[#CE2B37]" /></span>
            <span className="text-xs sm:text-sm text-gray-200"><span className="text-[#D4AF37] font-semibold">Restaurant Guru 2026</span> · Meilleur restaurant italien de Champs-sur-Marne</span>
          </a>
          <h1 className="font-serif-elegant text-4xl sm:text-5xl lg:text-6xl font-bold leading-none tracking-tight max-w-3xl fade-up-1">
            Bienvenue à l'<span className="text-[#D4AF37]">Italian Club</span>
          </h1>
          <p className="text-lg text-gray-300 mt-6 max-w-xl fade-up-2">
            {settings?.hero_subtitle || "L'Italie dans votre assiette, la musique dans votre cœur"}
          </p>
          <div className="flex flex-wrap gap-4 mt-10 fade-up-3">
            <Link to="/reserver" data-testid="hero-reserve-btn" className="btn-gold">Réserver une table</Link>
            <Link to="/la-carte" data-testid="hero-menu-btn" className="btn-outline-gold">Voir la carte</Link>
            <Link to="/soirees" data-testid="hero-events-btn" className="btn-outline-gold">Découvrir les soirées</Link>
            <Link to="/menus-semaine" data-testid="hero-weekly-btn" className="btn-outline-gold">Menu de la semaine</Link>
          </div>
        </div>
      </section>

      {/* HIGHLIGHTS */}
      <section className="max-w-7xl mx-auto px-6 py-24">
        <p className="gold-label mb-3">L'expérience Italian Club</p>
        <h2 className="font-serif-elegant text-2xl sm:text-3xl lg:text-4xl font-semibold mb-12">Un lieu, mille émotions</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {highlights.map(({ icon: Icon, title, text, to, span }) => (
            <Link key={title} to={to} data-testid={`highlight-${title.toLowerCase().replace(/[^a-z]/g, "-")}`} className={`ic-card p-8 group ${span || ""}`}>
              <Icon className="text-[#D4AF37] mb-5" size={28} />
              <h3 className="font-serif-elegant text-xl sm:text-2xl font-medium mb-2">{title}</h3>
              <p className="text-sm text-gray-400 leading-relaxed">{text}</p>
              <span className="inline-flex items-center gap-1 text-[#D4AF37] text-sm mt-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                Découvrir <ArrowRight size={14} />
              </span>
            </Link>
          ))}
        </div>
      </section>

      {/* À PROPOS */}
      <section className="max-w-7xl mx-auto px-6 py-24 grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-testid="about-section">
        <div>
          <p className="gold-label mb-3">À propos</p>
          <h2 className="font-serif-elegant text-2xl sm:text-3xl lg:text-4xl font-semibold mb-6">
            Un restaurant italien chaleureux &amp; élégant
          </h2>
          <p className="text-gray-300 leading-relaxed mb-8">{settings?.about_text}</p>
          <ul className="space-y-3 text-sm text-gray-300">
            <li className="flex items-center gap-3"><span className="w-1.5 h-1.5 rounded-full bg-[#D4AF37]" /> Terrasse extérieure avec fontaine</li>
            <li className="flex items-center gap-3"><span className="w-1.5 h-1.5 rounded-full bg-[#D4AF37]" /> 60 places en terrasse</li>
            <li className="flex items-center gap-3"><span className="w-1.5 h-1.5 rounded-full bg-[#D4AF37]" /> 150 places au total entre salle intérieure et terrasse</li>
            <li className="flex items-center gap-3"><span className="w-1.5 h-1.5 rounded-full bg-[#D4AF37]" /> Mariages, anniversaires, repas de groupe, événements privés et soirées professionnelles</li>
          </ul>
        </div>
        <div className="relative mb-8 lg:mb-0">
          <img src={IMAGES.terrace2} alt="Entrée de la terrasse" className="rounded-2xl w-full max-h-[80vh] object-cover border border-white/10" />
          <div className="absolute -bottom-6 left-3 sm:left-6 flex items-center gap-4 rounded-2xl border border-[#D4AF37]/40 bg-[#0b0b0b]/90 backdrop-blur-md p-4 pr-6 shadow-[0_10px_40px_rgba(0,0,0,0.6)]" data-testid="about-award-card">
            <img src={IMAGES.award2026} alt="Certificat Restaurant Guru 2026 — Meilleur restaurant italien" className="h-20 w-auto rounded-md bg-white" loading="lazy" />
            <div>
              <p className="text-[10px] tracking-[0.3em] uppercase text-[#D4AF37]">Restaurant Guru 2026</p>
              <p className="font-serif-elegant text-lg sm:text-xl font-semibold leading-tight mt-1">Meilleur restaurant italien</p>
              <p className="text-xs text-gray-400">dans Champs-sur-Marne</p>
            </div>
          </div>
        </div>
      </section>

      {/* MENU DE LA SEMAINE */}
      {weekly.length > 0 && (
        <section className="max-w-7xl mx-auto px-6 py-24" data-testid="home-weekly-section">
          <div className="flex items-end justify-between mb-12">
            <div>
              <p className="gold-label mb-3">24,90 € TTC — Entrée · Plat · Dessert · Café · Boisson</p>
              <h2 className="font-serif-elegant text-2xl sm:text-3xl lg:text-4xl font-semibold">Menu de la semaine</h2>
            </div>
            <Link to="/menus-semaine" className="hidden sm:inline-flex btn-outline-gold !py-2 text-sm">Voir en détail</Link>
          </div>
          {weekly.slice(0, 1).map((m) => (
            <div key={m.id} className="ic-card overflow-hidden grid grid-cols-1 md:grid-cols-2" data-testid={`home-weekly-card-${m.id}`}>
              {m.photo_url && <img src={m.photo_url} alt={m.title} className="h-64 md:h-full w-full object-cover" />}
              <div className="p-8">
                <h3 className="font-serif-elegant text-2xl font-semibold text-[#D4AF37]">{m.title}</h3>
                <div className="mt-5 space-y-4 text-sm">
                  <div><span className="gold-label">Entrées au choix</span><p className="text-gray-300 mt-1">{(m.entrees || []).join(" · ")}</p></div>
                  <div><span className="gold-label">Plats au choix</span><p className="text-gray-300 mt-1">{(m.plats || []).join(" · ")}</p></div>
                  <div><span className="gold-label">Desserts au choix</span><p className="text-gray-300 mt-1">{(m.desserts || []).join(" · ")}</p></div>
                </div>
                <div className="flex items-center justify-between mt-6 pt-5 border-t border-white/10">
                  <p className="text-2xl font-medium">{m.price.toFixed(2).replace(".", ",")} €</p>
                  <Link to={`/reserver?menu=${m.id}`} className="btn-gold !px-5 !py-2 text-sm">Réserver</Link>
                </div>
              </div>
            </div>
          ))}
        </section>
      )}

      {/* PROCHAINES SOIRÉES */}
      {events.length > 0 && (
        <section className="max-w-7xl mx-auto px-6 py-24" data-testid="home-events-section">
          <div className="flex items-end justify-between mb-12">
            <div>
              <p className="gold-label mb-3">Programme des soirées</p>
              <h2 className="font-serif-elegant text-2xl sm:text-3xl lg:text-4xl font-semibold">Prochaines soirées</h2>
            </div>
            <Link to="/soirees" className="hidden sm:inline-flex btn-outline-gold !py-2 text-sm">Tout le programme</Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
            {events.map((e) => (
              <Link to="/soirees" key={e.id} className="ic-card overflow-hidden flex flex-row md:flex-col" data-testid={`home-event-${e.id}`}>
                {e.photo_url && <img src={e.photo_url} alt={e.artist_name} className="w-[38%] shrink-0 md:w-full md:aspect-[9/16] object-cover bg-black" />}
                <div className="p-4 md:p-6 min-w-0">
                  <p className="gold-label">{e.event_type}</p>
                  <h3 className="font-serif-elegant text-lg md:text-xl font-medium mt-1 md:mt-2 leading-snug">{e.title}</h3>
                  <p className="text-sm text-gray-400 mt-1">{new Date(e.date + "T00:00:00").toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" })} · {e.time}</p>
                </div>
              </Link>
            ))}
          </div>
          <Link to="/soirees" className="sm:hidden btn-outline-gold !py-2 text-sm mt-6" data-testid="home-events-all-mobile">Tout le programme</Link>
        </section>
      )}
    </div>
  );
}
